import { useNavigate } from 'react-router-dom'
import type { Nation, Party, GovernmentType, ElectoralSystem } from '@/types'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

interface NationJoinCardProps {
  nation: Nation
  worldName: string
  partyCount: number
  userParty: Party | null
}

const governmentLabels: Record<GovernmentType, string> = {
  parliamentary: 'Parliamentary',
  semi_presidential: 'Semi-Presidential',
  presidential: 'Presidential',
}

const electoralLabels: Record<ElectoralSystem, string> = {
  nationwide_pr: 'Nationwide PR',
  regional_pr: 'Regional PR',
  mixed: 'Mixed',
}

export function NationJoinCard({ nation, worldName, partyCount, userParty }: NationJoinCardProps) {
  const navigate = useNavigate()

  const isFull = partyCount >= nation.max_parties
  const hasParty = userParty !== null

  const handleAction = () => {
    if (hasParty) {
      navigate(`/parties/${userParty.id}`)
    } else if (!isFull) {
      navigate(`/nations/${nation.id}/parties/new`)
    }
  }

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg">{nation.name}</CardTitle>
          {hasParty ? (
            <Badge variant="secondary">Your Party</Badge>
          ) : isFull ? (
            <Badge variant="outline" className="text-muted-foreground">
              Full - {partyCount}/{nation.max_parties}
            </Badge>
          ) : (
            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
              Open - {partyCount}/{nation.max_parties}
            </Badge>
          )}
        </div>
        <p className="text-muted-foreground text-sm">World: {worldName}</p>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col justify-between gap-4">
        <div className="text-muted-foreground text-sm">
          {governmentLabels[nation.government_type]} &bull; {electoralLabels[nation.electoral_system]}
        </div>

        {hasParty && (
          <div className="text-sm">
            <span className="text-muted-foreground">Your party:</span>{' '}
            <span className="font-medium">{userParty.name}</span>
          </div>
        )}

        <Button
          onClick={handleAction}
          disabled={!hasParty && isFull}
          variant={hasParty ? 'outline' : 'default'}
          className="w-full"
        >
          {hasParty ? 'View Party' : isFull ? 'Nation Full' : 'Create Party'}
        </Button>
      </CardContent>
    </Card>
  )
}
